'use client';

import { useMutation } from '@tanstack/react-query';

import type { SignupFieldEntry } from '../schemas/signup-custom-fields.schema';

import { signUpWithOtpAction } from '../server/sign-up-with-otp-action';

interface Credentials {
  name: string;
  email: string;
  // Optional (Phase D): secondary credential — customer may skip it.
  phoneNumber?: string;
  signupFields?: SignupFieldEntry[];
  children?: Array<{ firstName: string; lastName: string }>;
  emailConflictResolution?: 'use-existing' | 'override';
  // Forwarded when the user resolves a phone-mismatch banner (guest upgrade).
  phoneConflictResolution?: 'use-existing' | 'override';
}

// Same discriminated shape as SignUpMutationResult (password flow) so the OTP
// flow hook can reuse the identical banner/error handling.
export type OtpSignUpMutationResult =
  | { kind: 'success'; data: unknown }
  | { kind: 'email_mismatch'; emailHint: string }
  | { kind: 'phone_mismatch'; phoneHint: string }
  | { kind: 'phone_exists' }
  | { kind: 'email_exists' }
  | { kind: 'user_exists' };

export function useSignUpWithOtpAction() {
  const mutationKey = ['auth', 'sign-up-with-otp-action'];

  const mutationFn = async (
    params: Credentials,
  ): Promise<OtpSignUpMutationResult> => {
    // Phase D: phone is OPTIONAL here — the email is the verified anchor.
    const result = await signUpWithOtpAction({
      name: params.name,
      email: params.email,
      phoneNumber: params.phoneNumber,
      children: params.children,
      signupFields: params.signupFields,
      emailConflictResolution: params.emailConflictResolution,
      phoneConflictResolution: params.phoneConflictResolution,
    });

    if (result.success) {
      return { kind: 'success', data: result.data };
    }

    if ('code' in result) {
      if (result.code === 'email_mismatch') {
        return { kind: 'email_mismatch', emailHint: result.emailHint };
      }

      if (result.code === 'phone_mismatch') {
        return { kind: 'phone_mismatch', phoneHint: result.phoneHint };
      }

      if (result.code === 'phone_exists') {
        return { kind: 'phone_exists' };
      }

      if (result.code === 'email_exists') {
        return { kind: 'email_exists' };
      }

      if (result.code === 'user_exists') {
        return { kind: 'user_exists' };
      }
    }

    // Plain { success: false; error } branch (infra / send failures).
    const errorMessage = 'error' in result ? result.error : undefined;
    throw new Error(errorMessage || 'Account creation failed');
  };

  return useMutation({
    mutationKey,
    mutationFn,
  });
}
